import React, { useMemo, useState } from 'react';
import InstitutionLogo from './InstitutionLogo';

// Human labels + a small flag emoji for each ISO-2 code we ship parsers for.
// New countries added to the parser registry appear automatically in the
// dropdown; only ones listed here get a nice label + flag.
const COUNTRY_LABELS = {
  NG: { label: 'Nigeria', flag: '\uD83C\uDDF3\uD83C\uDDEC' },
  ZA: { label: 'South Africa', flag: '\uD83C\uDDFF\uD83C\uDDE6' },
  GB: { label: 'United Kingdom', flag: '\uD83C\uDDEC\uD83C\uDDE7' },
  AE: { label: 'United Arab Emirates', flag: '\uD83C\uDDE6\uD83C\uDDEA' },
  KE: { label: 'Kenya', flag: '\uD83C\uDDF0\uD83C\uDDEA' },
  GH: { label: 'Ghana', flag: '\uD83C\uDDEC\uD83C\uDDED' },
  SG: { label: 'Singapore', flag: '\uD83C\uDDF8\uD83C\uDDEC' },
  US: { label: 'United States', flag: '\uD83C\uDDFA\uD83C\uDDF8' },
  CA: { label: 'Canada', flag: '\uD83C\uDDE8\uD83C\uDDE6' },
};

// Compact "browse by country" panel shown at the top of the Import modal.
// Users pick their country from a dropdown and see the ten institutions we
// already read files from in that market — a trust signal before they
// upload anything.
const InstitutionImportCountryList = ({ allInstitutions = [], userCountryIso = '' }) => {
  // Build the set of countries actually present in the registry so the
  // dropdown never lists a market we haven't shipped.
  const countries = useMemo(() => {
    const codes = Array.from(new Set(allInstitutions.map((i) => i.country_code)));
    return codes
      .filter((c) => COUNTRY_LABELS[c])
      .sort((a, b) => COUNTRY_LABELS[a].label.localeCompare(COUNTRY_LABELS[b].label));
  }, [allInstitutions]);

  const [selected, setSelected] = useState(() => {
    if (userCountryIso && countries.includes(userCountryIso)) return userCountryIso;
    return countries[0] || '';
  });

  const listed = useMemo(
    () => allInstitutions.filter((i) => i.country_code === selected),
    [allInstitutions, selected],
  );

  if (countries.length === 0) return null;

  return (
    <div
      className="mb-4 rounded-md border border-slate-200 bg-white px-3 py-3"
      data-testid="institution-import-country-filter"
    >
      <div className="flex items-center justify-between gap-3">
        <label htmlFor="ii-country" className="text-xs text-slate-600">
          Institutions we read in
        </label>
        <select
          id="ii-country"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="text-sm rounded-md border border-slate-300 bg-white px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          data-testid="institution-import-country-select"
        >
          {countries.map((c) => (
            <option key={c} value={c}>
              {COUNTRY_LABELS[c].flag}&nbsp; {COUNTRY_LABELS[c].label}
            </option>
          ))}
        </select>
      </div>
      <ul
        className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-700"
        data-testid="institution-import-country-list"
      >
        {listed.map((i) => (
          <li
            key={i.institution_id}
            className="flex items-center gap-2 py-0.5"
            data-testid={`institution-import-country-list-item-${i.institution_id}`}
          >
            <InstitutionLogo institutionId={i.institution_id} name={i.display_name} />
            <span className="truncate">{i.display_name}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-slate-500">
        Don&rsquo;t see yours? Upload the file and we&rsquo;ll add your institution to the batch for consideration at our next review. In the meantime, use any of the other options above to add your investments.
      </p>
    </div>
  );
};

export default InstitutionImportCountryList;
